import { Typography, message as antdMessage } from 'antd';
import React, { useCallback, useState } from 'react';
import { FiTrash2 } from 'react-icons/fi';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import WarningModal from 'components/modals/WarningModal';
import {
  removeBackOfficeInvitation,
  resendBackOfficeInvitation
} from 'store/modules/backOfficeInvitations/actions';
import BackOfficeInvitationsActionType from 'store/modules/backOfficeInvitations/constants';
import { makeSelectInvitationsByEmail } from 'store/modules/backOfficeInvitations/selectors';
import { useIsLoading } from 'store/modules/loading/hooks';
import BackOfficeInvitation, { BackOfficeInvitationId } from 'types/models/back-office-invitation';
import { useInvitationDescriptor } from 'utils/Invitation/use-invitation-descriptor';

import './styles.less';

// ==============================
// TYPES
// ==============================
interface BackOfficeUserInvitationCardProps {
  invitation: BackOfficeInvitation;
  resendInvitation: (id: BackOfficeInvitationId) => void;
  removeInvitation: (invitation: BackOfficeInvitation) => void;
}

// ==============================
// COMPONENTS
// ==============================
function BackOfficeUserInvitationCard({
  invitation,
  resendInvitation,
  removeInvitation
}: BackOfficeUserInvitationCardProps) {
  const { enterpriseName, role } = useInvitationDescriptor(invitation);

  return (
    <div className="BackOfficeUserInvitation">
      <div className="BackOfficeUserInvitation-top">
        <div>
          <div>{enterpriseName}</div>
          <div className="BackOfficeUserInvitation-subtitle">{role} · Pending</div>
        </div>
        <FiTrash2 size={20} onClick={() => removeInvitation(invitation)} />
      </div>
      <div className="BackOfficeUserInvitation-footer">
        <div onClick={() => resendInvitation(invitation.id)}>Resend Invite</div>
      </div>
    </div>
  );
}

interface BackOfficeUserInvitationsProps {
  email?: string;
}

/** Invitation card list */
export default function BackOfficeUserInvitations({
  email
}: BackOfficeUserInvitationsProps) {
  const dispatch = useDispatch();
  const invitations = useSelector(
    makeSelectInvitationsByEmail(email),
    shallowEqual
  );
  const [
    isRemoveConfirmationModalVisible,
    setIsRemoveConfirmationModalVisible
  ] = useState<boolean>(false);
  const [invitationToRemove, setInvitationToRemove] = useState<BackOfficeInvitation>();

  const resendInvitation = useCallback(
    (id: any) => dispatch(resendBackOfficeInvitation(id)),
    [dispatch]
  );
  const onRemove = useCallback((invitation: BackOfficeInvitation) => {
    setInvitationToRemove(invitation);
    setIsRemoveConfirmationModalVisible(true);
  }, []);
  const removeInvitation = useCallback(() => {
    if (invitationToRemove) {
      dispatch(
        removeBackOfficeInvitation(invitationToRemove.id, () => {
          setIsRemoveConfirmationModalVisible(false);
          antdMessage.success('Invitation removed successfully');
        })
      );
    }
  }, [dispatch, invitationToRemove]);
  const isRemovingInvitation = useIsLoading(BackOfficeInvitationsActionType.REMOVE_BACK_OFFICE_INVITATION_REQUEST);

  const { enterpriseName } = useInvitationDescriptor(invitationToRemove);

  return (
    <div className="BackOfficeUserInvitations">
      <Typography.Text strong>Invitations</Typography.Text>
      {invitations.length
        ? invitations.map((invitation) => (
          <BackOfficeUserInvitationCard
            key={invitation.id}
            invitation={invitation}
            resendInvitation={resendInvitation}
            removeInvitation={onRemove}
          />
        ))
        : 'No invitations'}
      <WarningModal
        isOpen={isRemoveConfirmationModalVisible}
        title={`Are you sure you want to remove the invitation to ${enterpriseName}?`}
        onDismiss={() => setIsRemoveConfirmationModalVisible(false)}
        onConfirm={removeInvitation}
        confirmText="Remove"
        isConfirmButtonLoading={isRemovingInvitation}
      >
        The invitation link sent to their email will no longer work.
      </WarningModal>
    </div>
  );
}
